// @ts-nocheck
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { getFasteners, GetFastenersCollectionApiParameter } from "@store/Framework/Services/FastenerSercice";

export interface LoadFastenersParameter {
    tableId: string;
    productId?: string;
    isShellEdit?: boolean;
    pageSize?: number;
    page?: number;
    length?: string;
    boxCount?: string;
    headDiameter?: string;
    thread?: string;
    driveType?: string;
    point?: string;
    head?: string;
    finish?: string;
    additionalSort?: (products: ProductModel[]) => ProductModel[];
}

interface Props {
    getProductCollectionParameter: GetFastenersCollectionApiParameter;
    products?: ProductModel[];
    productInfos?: ProductInfo[];
    pricingLoaded?: true;
    errorMessage?: string;
    dataViewHasProducts?: boolean;
}

type HandlerType = Handler<LoadFastenersParameter, Props>;

export const SetFastenersParameter: HandlerType = props => {
    const { tableId, isShellEdit, additionalSort, ...apiParameter } = props.parameter;
    const { productId } = apiParameter;

    if (!productId) {
        return;
    }

    props.getProductCollectionParameter = {
        ...apiParameter,
        productId,
        pageSize: apiParameter.pageSize ?? 5,
        page: apiParameter.page ?? 1,
    };
};

export const LoadFastenerProducts: HandlerType = async props => {
    const { getProductCollectionParameter } = props;
    if (!getProductCollectionParameter) {
        return false;
    }

    const productsDataViewState = getProductsDataView(props.getState(), getProductCollectionParameter);
    if (productsDataViewState.isLoading) {
        props.dispatch({
            type: "Components/ProductInfoLists/SetWaitingForData",
            id: props.parameter.tableId,
            parameter: getProductCollectionParameter,
        });
        return false;
    }

    props.dataViewHasProducts = !!productsDataViewState.value && productsDataViewState.value.length > 0;
    if (props.dataViewHasProducts) {
        props.products = [...productsDataViewState.value!];
        return;
    }

    props.dispatch({
        type: "Data/Products/BeginLoadProducts",
        parameter: getProductCollectionParameter,
    });

    try {
        const result = await getFasteners(getProductCollectionParameter);
        props.products = result.products ?? [];

        props.dispatch({
            type: "Store/Data/Fasteners/CompleteLoadFastenersCollections",
            collection: result,
        });

        props.dispatch({
            type: "Data/Products/CompleteLoadProducts",
            parameter: getProductCollectionParameter,
            collection: result,
        });
    } catch (error) {
        props.errorMessage = error;
    }
};

export const SortProducts: HandlerType = props => {
    if (!props.products || !props.parameter.additionalSort) {
        return;
    }

    props.products = props.parameter.additionalSort(props.products);
};

export const SetUpProductInfos: HandlerType = props => {
    if (!props.products) {
        return;
    }

    props.productInfos = [];
    for (const product of props.products) {
        props.productInfos.push(createFromProduct(product));
    }
};

export const DispatchCompleteLoadProducts: HandlerType = props => {
    if (props.dataViewHasProducts && props.parameter.isShellEdit) {
        return;
    }

    props.dispatch({
        type: "Components/ProductInfoLists/CompleteLoadProductInfoList",
        id: props.parameter.tableId,
        parameter: props.getProductCollectionParameter,
        productInfos: props.productInfos ?? [],
        errorMessage: props.errorMessage,
    });
};

export const LoadRealTimePrices: HandlerType = async props => {
    if (
        !props.productInfos ||
        props.productInfos.length === 0 ||
        (props.dataViewHasProducts && props.parameter.isShellEdit)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productInfos,
            onSuccess: realTimePricing => {
                props.dispatch({
                    type: "Components/ProductInfoLists/CompleteLoadRealTimePricing",
                    id: props.parameter.tableId,
                    realTimePricing,
                });
                props.pricingLoaded = true;
            },
            onError: () => {
                props.dispatch({
                    type: "Components/ProductInfoLists/FailedLoadRealTimePricing",
                    id: props.parameter.tableId,
                });
                props.pricingLoaded = true;
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const chain = [
    SetFastenersParameter,
    LoadFastenerProducts,
    SortProducts,
    SetUpProductInfos,
    DispatchCompleteLoadProducts,
    LoadRealTimePrices,
];

const loadFasteners = createHandlerChainRunner(chain, "LoadFasteners");
export default loadFasteners;
